import { http } from "@/lib/http";

import type {
  FloatingIP,
  FloatingIPAssociateRequest,
  FloatingIPCreateRequest,
  NetworkCreateRequest,
  NetworkTopology,
  NetworkUpdateRequest,
  NeutronNetwork,
  Port,
  PortCreateRequest,
  PortForwarding,
  PortForwardingCreateRequest,
  PortForwardingUpdateRequest,
  PortUpdateRequest,
  QosBandwidthLimitRule,
  QosBandwidthLimitRuleCreateRequest,
  QosPolicy,
  QosPolicyCreateRequest,
  RbacPolicy,
  RbacPolicyCreateRequest,
  Router,
  RouterCreateRequest,
  RouterInterface,
  RouterInterfaceRequest,
  RouterTransfer,
  RouterUpdateRequest,
  SecurityGroup,
  SecurityGroupCreateRequest,
  SecurityGroupRule,
  SecurityGroupRuleCreateRequest,
  SecurityGroupUpdateRequest,
  StaticRoute,
  Subnet,
  SubnetCreateRequest,
  SubnetUpdateRequest,
} from "./types";

// --- Networks ---

export async function listNetworks(): Promise<NeutronNetwork[]> {
  const { data } = await http.get<NeutronNetwork[]>("/network/networks");
  return data;
}

export async function getNetwork(networkId: string): Promise<NeutronNetwork> {
  const { data } = await http.get<NeutronNetwork>(
    `/network/networks/${networkId}`,
  );
  return data;
}

export async function createNetwork(
  payload: NetworkCreateRequest,
): Promise<NeutronNetwork> {
  const { data } = await http.post<NeutronNetwork>(
    "/network/networks",
    payload,
  );
  return data;
}

export async function updateNetwork(
  networkId: string,
  payload: NetworkUpdateRequest,
): Promise<NeutronNetwork> {
  const { data } = await http.put<NeutronNetwork>(
    `/network/networks/${networkId}`,
    payload,
  );
  return data;
}

// --- Ports ---

export async function listPorts(networkId?: string): Promise<Port[]> {
  const { data } = await http.get<Port[]>("/network/ports", {
    params: networkId ? { network_id: networkId } : undefined,
  });
  return data;
}

export async function createPort(payload: PortCreateRequest): Promise<Port> {
  const { data } = await http.post<Port>("/network/ports", payload);
  return data;
}

export async function updatePort(
  portId: string,
  payload: PortUpdateRequest,
): Promise<Port> {
  const { data } = await http.put<Port>(`/network/ports/${portId}`, payload);
  return data;
}

export async function deletePort(portId: string): Promise<void> {
  await http.delete(`/network/ports/${portId}`);
}

// --- Router data transfer ---

export async function getRouterTransfer(
  routerId: string,
  hours: number,
): Promise<RouterTransfer> {
  const { data } = await http.get<RouterTransfer>(
    `/network/routers/${routerId}/transfer`,
    { params: { hours } },
  );
  return data;
}

// --- Router static routes ---

export async function listRouterRoutes(
  routerId: string,
): Promise<StaticRoute[]> {
  const { data } = await http.get<StaticRoute[]>(
    `/network/routers/${routerId}/routes`,
  );
  return data;
}

export async function replaceRouterRoutes(
  routerId: string,
  routes: StaticRoute[],
): Promise<StaticRoute[]> {
  const { data } = await http.put<StaticRoute[]>(
    `/network/routers/${routerId}/routes`,
    { routes },
  );
  return data;
}

export async function addRouterRoutes(
  routerId: string,
  routes: StaticRoute[],
): Promise<StaticRoute[]> {
  const { data } = await http.post<StaticRoute[]>(
    `/network/routers/${routerId}/routes`,
    { routes },
  );
  return data;
}

export async function removeRouterRoutes(
  routerId: string,
  routes: StaticRoute[],
): Promise<StaticRoute[]> {
  const { data } = await http.delete<StaticRoute[]>(
    `/network/routers/${routerId}/routes`,
    { data: { routes } },
  );
  return data;
}

// --- Floating-IP port forwarding ---

export async function listPortForwardings(
  floatingIpId: string,
): Promise<PortForwarding[]> {
  const { data } = await http.get<PortForwarding[]>(
    `/network/floating-ips/${floatingIpId}/port-forwardings`,
  );
  return data;
}

export async function createPortForwarding(
  floatingIpId: string,
  payload: PortForwardingCreateRequest,
): Promise<PortForwarding> {
  const { data } = await http.post<PortForwarding>(
    `/network/floating-ips/${floatingIpId}/port-forwardings`,
    payload,
  );
  return data;
}

export async function updatePortForwarding(
  floatingIpId: string,
  pfId: string,
  payload: PortForwardingUpdateRequest,
): Promise<PortForwarding> {
  const { data } = await http.put<PortForwarding>(
    `/network/floating-ips/${floatingIpId}/port-forwardings/${pfId}`,
    payload,
  );
  return data;
}

export async function deletePortForwarding(
  floatingIpId: string,
  pfId: string,
): Promise<void> {
  await http.delete(
    `/network/floating-ips/${floatingIpId}/port-forwardings/${pfId}`,
  );
}

export async function deleteNetwork(networkId: string): Promise<void> {
  await http.delete(`/network/networks/${networkId}`);
}

// --- Subnets ---

export async function listSubnets(): Promise<Subnet[]> {
  const { data } = await http.get<Subnet[]>("/network/subnets");
  return data;
}

export async function createSubnet(
  payload: SubnetCreateRequest,
): Promise<Subnet> {
  const { data } = await http.post<Subnet>("/network/subnets", payload);
  return data;
}

export async function updateSubnet(
  subnetId: string,
  payload: SubnetUpdateRequest,
): Promise<Subnet> {
  const { data } = await http.put<Subnet>(
    `/network/subnets/${subnetId}`,
    payload,
  );
  return data;
}

export async function deleteSubnet(subnetId: string): Promise<void> {
  await http.delete(`/network/subnets/${subnetId}`);
}

// --- Routers ---

export async function listRouters(): Promise<Router[]> {
  const { data } = await http.get<Router[]>("/network/routers");
  return data;
}

export async function createRouter(
  payload: RouterCreateRequest,
): Promise<Router> {
  const { data } = await http.post<Router>("/network/routers", payload);
  return data;
}

export async function updateRouter(
  routerId: string,
  payload: RouterUpdateRequest,
): Promise<Router> {
  const { data } = await http.put<Router>(
    `/network/routers/${routerId}`,
    payload,
  );
  return data;
}

export async function deleteRouter(routerId: string): Promise<void> {
  await http.delete(`/network/routers/${routerId}`);
}

export async function addRouterInterface(
  routerId: string,
  payload: RouterInterfaceRequest,
): Promise<RouterInterface> {
  const { data } = await http.post<RouterInterface>(
    `/network/routers/${routerId}/interfaces`,
    payload,
  );
  return data;
}

export async function removeRouterInterface(
  routerId: string,
  payload: RouterInterfaceRequest,
): Promise<void> {
  await http.delete(`/network/routers/${routerId}/interfaces`, {
    data: payload,
  });
}

// --- Floating IPs ---

export async function listFloatingIps(): Promise<FloatingIP[]> {
  const { data } = await http.get<FloatingIP[]>("/network/floating-ips");
  return data;
}

export async function createFloatingIp(
  payload: FloatingIPCreateRequest,
): Promise<FloatingIP> {
  const { data } = await http.post<FloatingIP>(
    "/network/floating-ips",
    payload,
  );
  return data;
}

export async function deleteFloatingIp(floatingIpId: string): Promise<void> {
  await http.delete(`/network/floating-ips/${floatingIpId}`);
}

export async function associateFloatingIp(
  floatingIpId: string,
  payload: FloatingIPAssociateRequest,
): Promise<FloatingIP> {
  const { data } = await http.post<FloatingIP>(
    `/network/floating-ips/${floatingIpId}/associate`,
    payload,
  );
  return data;
}

export async function disassociateFloatingIp(
  floatingIpId: string,
): Promise<FloatingIP> {
  const { data } = await http.post<FloatingIP>(
    `/network/floating-ips/${floatingIpId}/disassociate`,
  );
  return data;
}

// --- Security groups ---

export async function listSecurityGroups(): Promise<SecurityGroup[]> {
  const { data } = await http.get<SecurityGroup[]>("/network/security-groups");
  return data;
}

export async function createSecurityGroup(
  payload: SecurityGroupCreateRequest,
): Promise<SecurityGroup> {
  const { data } = await http.post<SecurityGroup>(
    "/network/security-groups",
    payload,
  );
  return data;
}

export async function updateSecurityGroup(
  securityGroupId: string,
  payload: SecurityGroupUpdateRequest,
): Promise<SecurityGroup> {
  const { data } = await http.put<SecurityGroup>(
    `/network/security-groups/${securityGroupId}`,
    payload,
  );
  return data;
}

export async function deleteSecurityGroup(
  securityGroupId: string,
): Promise<void> {
  await http.delete(`/network/security-groups/${securityGroupId}`);
}

export async function createSecurityGroupRule(
  securityGroupId: string,
  payload: SecurityGroupRuleCreateRequest,
): Promise<SecurityGroupRule> {
  const { data } = await http.post<SecurityGroupRule>(
    `/network/security-groups/${securityGroupId}/rules`,
    payload,
  );
  return data;
}

export async function updateSecurityGroupRule(
  ruleId: string,
  payload: SecurityGroupRuleCreateRequest,
): Promise<SecurityGroupRule> {
  const { data } = await http.put<SecurityGroupRule>(
    `/network/security-group-rules/${ruleId}`,
    payload,
  );
  return data;
}

export async function deleteSecurityGroupRule(ruleId: string): Promise<void> {
  await http.delete(`/network/security-group-rules/${ruleId}`);
}

// --- RBAC policies ---

export async function listRbacPolicies(): Promise<RbacPolicy[]> {
  const { data } = await http.get<RbacPolicy[]>("/network/rbac-policies");
  return data;
}

export async function createRbacPolicy(
  payload: RbacPolicyCreateRequest,
): Promise<RbacPolicy> {
  const { data } = await http.post<RbacPolicy>(
    "/network/rbac-policies",
    payload,
  );
  return data;
}

export async function deleteRbacPolicy(policyId: string): Promise<void> {
  await http.delete(`/network/rbac-policies/${policyId}`);
}

// --- QoS policies + rules ---

export async function listQosPolicies(): Promise<QosPolicy[]> {
  const { data } = await http.get<QosPolicy[]>("/network/qos-policies");
  return data;
}

export async function createQosPolicy(
  payload: QosPolicyCreateRequest,
): Promise<QosPolicy> {
  const { data } = await http.post<QosPolicy>(
    "/network/qos-policies",
    payload,
  );
  return data;
}

export async function deleteQosPolicy(policyId: string): Promise<void> {
  await http.delete(`/network/qos-policies/${policyId}`);
}

export async function listQosBandwidthLimitRules(
  policyId: string,
): Promise<QosBandwidthLimitRule[]> {
  const { data } = await http.get<QosBandwidthLimitRule[]>(
    `/network/qos-policies/${policyId}/bandwidth-limit-rules`,
  );
  return data;
}

export async function createQosBandwidthLimitRule(
  policyId: string,
  payload: QosBandwidthLimitRuleCreateRequest,
): Promise<QosBandwidthLimitRule> {
  const { data } = await http.post<QosBandwidthLimitRule>(
    `/network/qos-policies/${policyId}/bandwidth-limit-rules`,
    payload,
  );
  return data;
}

export async function deleteQosBandwidthLimitRule(
  policyId: string,
  ruleId: string,
): Promise<void> {
  await http.delete(
    `/network/qos-policies/${policyId}/bandwidth-limit-rules/${ruleId}`,
  );
}

// --- Topology ---

export async function getTopology(): Promise<NetworkTopology> {
  const { data } = await http.get<NetworkTopology>("/network/topology");
  return data;
}
